import React, { useEffect, useState } from 'react';
import api from './api';
import VerificationLog from './VerificationLog';
import ReceivingHistoryLog from './ReceivingHistoryLog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Cell } from 'recharts';

const ACCENTS = {
  blue: { main: 'rgb(65, 159, 196)', dark: 'rgb(43, 106, 131)' },
  red: { main: 'rgb(196, 65, 65)', dark: 'rgb(131, 43, 43)' },
};

export default function StatsPage({ user, msalInstance }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [accentTheme, setAccentTheme] = useState('blue');
  const accent = ACCENTS[accentTheme].main;
  const accentDark = ACCENTS[accentTheme].dark;
  const [globalStats, setGlobalStats] = useState(null);
  const [globalExpanded, setGlobalExpanded] = useState(false);
  const [csvConfirmation, setCsvConfirmation] = useState('');
  const getAuthHeaders = async () => {
    const authProvider = localStorage.getItem('authProvider') || 'microsoft';
    if (authProvider === 'google') {
      const token = localStorage.getItem('authToken');
      return { Authorization: `Bearer ${token}` };
    }
    const configRes = await api.get('/api/config/azure');
    const { clientId } = configRes.data;
    const accounts = msalInstance.getAllAccounts();
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/access_as_user`],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  };
const downloadCsv = () => {
  if (!stats?.allVerifications) return;

  const q = val => `"${String(val).replace(/"/g, '""')}"`;

  const formatDuration = (mins) => {
    const m = Math.floor(mins);
    const s = Math.round((mins - m) * 60);
    return `${m}m ${s}s`;
  };

  const dateRangeLabel = from && to
    ? `${from} to ${to}`
    : from ? `from ${from}`
    : to ? `to ${to}`
    : 'All time';

  const summary = [
    ['2Check Verification Report'],
    [`Date range:`, dateRangeLabel],
    [`Generated:`, new Date().toLocaleString()],
    [`Total orders:`, stats.totalOrders],
    [`Total items:`, stats.totalItems.toLocaleString()],
    [`Avg time per order:`, formatDuration(stats.avgMinutesPerOrder)],
    [],
  ];

  const headers = [
    'Order Number',
    'Order Name',
    'Date Verified',
    'Day of Week',
    'Verified By',
    'Time',
    'Type',
    '# Different Products',
    '# Individual Items',
    'Duration (min)',
    'Duration (formatted)',
    'PDF Link',
  ];

  const rows = stats.allVerifications.map(v => {
    const [year, month, day] = v.dateVerified.split('-').map(Number);
    const dow = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date(year, month - 1, day).getDay()];
    return [
      q(v.orderId),
      q(v.orderName),
      q(v.dateVerified),
      q(dow),
      q(v.verifiedBy),
      q(v.timeVerified),
      q(v.verificationType === 'level2' ? 'Level 2 (Full)' : 'Level 1'),
      q(v.uniqueItems),
      q(v.totalItems),
      q(v.durationMinutes.toFixed(1)),
      q(formatDuration(v.durationMinutes)),
      q(v.pdfUrl),
    ];
  });

  const summaryCsv = summary.map(r => r.map(cell => q(String(cell))).join(','));
  const dataCsv = [headers.map(q).join(','), ...rows.map(r => r.join(','))];
  const csv = [...summaryCsv, ...dataCsv].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const fromPart = from || 'start';
  const toPart = to || new Date().toISOString().slice(0, 10);
  link.download = `2check-report-${fromPart}-to-${toPart}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  setCsvConfirmation(`Downloaded ${stats.allVerifications.length} rows`);
  setTimeout(() => setCsvConfirmation(''), 3000);
};
const fetchStats = async () => {
  setLoading(true);
  try {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    params.append('timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    
    const res = await api.get(`/api/stats?${params}`, { headers });
    setStats(res.data);
    const globalRes = await api.get('/api/stats/global', { headers });
setGlobalStats(globalRes.data);
  } catch (err) {
    console.error('Failed to load stats:', err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => { fetchStats(); }, []);

  if (loading) return <div style={s.loading}>Loading stats…</div>;
  if (!stats) return <div style={s.loading}>Failed to load stats.</div>;

  return (
    <div style={s.page}>
      <style>{`
        @keyframes statsFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes statsPop {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        .stats-section { animation: statsFadeUp 0.35s ease both; }
        .stats-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .stats-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
        .stats-btn { transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease; }
        .stats-btn:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.12); }
        .stats-btn:active { transform: scale(0.96); }
        .stats-swatch { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .stats-swatch:hover { transform: scale(1.15); }
        .stats-expanded { animation: statsPop 0.22s ease both; transform-origin: top; }
        .stats-csv-confirm { animation: statsFadeUp 0.25s ease both; }
        @media (prefers-reduced-motion: reduce) {
          .stats-section, .stats-card, .stats-btn, .stats-swatch, .stats-expanded, .stats-csv-confirm {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
      <div style={s.titleRow}>
        <h1 style={s.title}>Verification Stats</h1>
        <div style={s.themeToggle} role="group" aria-label="Chart color">
          <button
            className="stats-swatch"
            style={{ ...s.themeSwatch, background: ACCENTS.blue.main, boxShadow: accentTheme === 'blue' ? '0 0 0 2px #fff, 0 0 0 4px rgb(65, 159, 196)' : 'none' }}
            onClick={() => setAccentTheme('blue')}
            aria-label="Blue charts"
            aria-pressed={accentTheme === 'blue'}
          />
          <button
            className="stats-swatch"
            style={{ ...s.themeSwatch, background: ACCENTS.red.main, boxShadow: accentTheme === 'red' ? '0 0 0 2px #fff, 0 0 0 4px rgb(196, 65, 65)' : 'none' }}
            onClick={() => setAccentTheme('red')}
            aria-label="Red charts"
            aria-pressed={accentTheme === 'red'}
          />
        </div>
      </div>
{globalStats && (
  <div style={{ ...s.globalBanner, background: `linear-gradient(135deg, ${accent}, ${accent})` }} onClick={() => setGlobalExpanded(e => !e)}>
    <div style={s.bannerRow}>
      <span style={s.globalTitle}>
        All-time 2Check stats
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <div style={s.globalStats}>
          <div style={s.globalStat}>
            <span style={s.globalValue}>{globalStats.totalOrders.toLocaleString()}</span>
            <span style={s.globalLabel}>Orders verified</span>
          </div>
          <div style={s.globalDivider} />
          <div style={s.globalStat}>
            <span style={s.globalValue}>{globalStats.totalItems.toLocaleString()}</span>
            <span style={s.globalLabel}>Items scanned</span>
          </div>
        </div>
        <span style={{
  display: 'inline-block',
  width: 0,
  height: 0,
  borderLeft: '5px solid transparent',
  borderRight: '5px solid transparent',
  borderTop: '6px solid rgba(255,255,255,0.8)',
  transform: globalExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.25s',
  marginBottom: globalExpanded ? 0 : 2,
}} />
      </div>
    </div>

    {globalExpanded && (
      <div className="stats-expanded" style={s.expandedRow}>
        <div style={s.expandedCard}>
          <span style={s.expandedVal}>{(globalStats.totalIndividualItems ?? 0).toLocaleString()}</span>
          <span style={s.expandedLbl}>Individual items</span>
          <span style={s.expandedSub}>scanned one by one</span>
        </div>
        <div style={s.expandedCard}>
          <span style={s.expandedVal}>{(globalStats.totalLots ?? 0).toLocaleString()}</span>
          <span style={s.expandedLbl}>Lots / cartons</span>
          <span style={s.expandedSub}>scanned as sets</span>
        </div>
        <div style={s.expandedCard}>
          <span style={s.expandedVal}>{(globalStats.totalUniqueItems ?? 0).toLocaleString()}</span>
          <span style={s.expandedLbl}>Unique products</span>
          <span style={s.expandedSub}>distinct SKUs</span>
        </div>
      </div>
    )}
  </div>
)}
      {/* Date filter */}
      <div style={s.filterRow}>
        <label style={s.label}>From</label>
        <input type="date" style={s.dateInput} value={from} onChange={e => setFrom(e.target.value)} />
        <label style={s.label}>To</label>
        <input type="date" style={s.dateInput} value={to} onChange={e => setTo(e.target.value)} />
        <button className="stats-btn" style={{ ...s.filterBtn, background: accent }} onClick={fetchStats}>Apply</button>
        <button className="stats-btn" style={s.clearBtn} onClick={() => { setFrom(''); setTo(''); setTimeout(fetchStats, 0); }}>Clear</button>
        <button className="stats-btn" style={{ ...s.csvBtn, background: accentDark }} onClick={downloadCsv}>⬇ Download CSV</button>
{csvConfirmation && (
  <span className="stats-csv-confirm" style={{ fontSize: 13, color: '#22a06b', fontWeight: 600 }}>
    ✓ {csvConfirmation}
  </span>
)}
      </div>

      {/* Summary cards */}
      <div style={s.cards}>
        <Card label="Total Orders Verified" value={stats.totalOrders} accent={accent} />
        <Card label="Total Items Scanned" value={stats.totalItems.toLocaleString()} accent={accent} />
        <Card label="Avg Time per Order" value={`${stats.avgMinutesPerOrder.toFixed(1)} min`} accent={accent} />
      </div>

      {/* Orders over time */}
      <Section title="Orders Verified Over Time">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={stats.byDay}>
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Line type="monotone" dataKey="orders" stroke={accent} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Section>

      {/* By user */}
      <Section title="Orders by User">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={stats.byUser}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="orders" fill={accent} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <table style={s.table}>
          <thead>
            <tr>{['User', 'Orders', 'Items', 'Avg Time'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {stats.byUser.map(u => (
              <tr key={u.name} style={s.tr}>
                <td style={s.td}>{u.name}</td>
                <td style={s.td}>{u.orders}</td>
                <td style={s.td}>{u.items.toLocaleString()}</td>
                <td style={s.td}>{u.avgMinutes.toFixed(1)} min</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      {/* Peak hours */}
<Section title="Peak Verification Hours">
  <p style={{ fontSize: 13, color: '#888', marginBottom: 16, marginTop: -8 }}>
    Number of orders verified by hour of day
  </p>
  <ResponsiveContainer width="100%" height={220}>
    <BarChart data={stats.byHour}>
      <XAxis 
        dataKey="label" 
        tick={{ fontSize: 10 }} 
        interval={1}
      />
      <YAxis allowDecimals={false} />
      <Tooltip formatter={(val) => [`${val} orders`, 'Verified']} />
      <Bar dataKey="orders" radius={[3, 3, 0, 0]}>
        {stats.byHour.map((entry, i) => (
          <Cell
            key={i}
            fill={
              entry.orders === Math.max(...stats.byHour.map(h => h.orders))
                ? accentDark
                : accent
            }
          />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
{stats.byHour.length > 0 && (() => {
  const maxOrders = Math.max(...stats.byHour.map(h => h.orders));
  if (maxOrders === 0) return null;
  const peaks = stats.byHour.filter(h => h.orders === maxOrders);
  return (
    <p style={{ fontSize: 13, color: '#555', marginTop: 12 }}>
      Peak time{peaks.length > 1 ? 's are' : ' is'}{' '}
      {peaks.map((p, i) => (
        <span key={i}>
          {i > 0 && i === peaks.length - 1 ? ' and ' : i > 0 ? ', ' : ''}
          <strong>{p.label}</strong>
        </span>
      ))}
      {' '}with <strong>{maxOrders}</strong> order{maxOrders !== 1 ? 's' : ''} verified.
    </p>
  );
})()}
</Section>{/* Day of week heatmap */}
<Section title="Verification Activity by Day of Week">
  <p style={{ fontSize: 13, color: '#888', marginBottom: 16, marginTop: -8 }}>
    Number of orders verified by day
  </p>
  <ResponsiveContainer width="100%" height={220}>
    <BarChart data={(() => {
      const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const counts = Array(7).fill(0);
      stats.allVerifications.forEach(v => {
  const [year, month, day] = v.dateVerified.split('-').map(Number);
  const dow = new Date(year, month - 1, day).getDay(); // local time, no UTC shift
  if (!isNaN(dow)) counts[dow]++;
});
      const max = Math.max(...counts);
      return days.map((name, i) => ({ name: name.slice(0, 3), orders: counts[i], isMax: counts[i] === max && max > 0 }));
    })()}>
      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
      <YAxis allowDecimals={false} />
      <Tooltip formatter={(val) => [`${val} orders`, 'Verified']} />
      <Bar dataKey="orders" radius={[3, 3, 0, 0]}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((_, i) => {
          const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
          const counts = Array(7).fill(0);
       stats.allVerifications.forEach(v => {
  const [year, month, day] = v.dateVerified.split('-').map(Number);
  const dow = new Date(year, month - 1, day).getDay(); // local time, no UTC shift
  if (!isNaN(dow)) counts[dow]++;
});
          const max = Math.max(...counts);
          return <Cell key={i} fill={counts[i] === max && max > 0 ? accentDark : accent} />;
        })}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
  {(() => {
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const counts = Array(7).fill(0);
stats.allVerifications.forEach(v => {
  const [year, month, day] = v.dateVerified.split('-').map(Number);
  const dow = new Date(year, month - 1, day).getDay(); // local time, no UTC shift
  if (!isNaN(dow)) counts[dow]++;
});
    const maxCount = Math.max(...counts);
    if (maxCount === 0) return null;
    const peakDay = days[counts.indexOf(maxCount)];
    return (
      <p style={{ fontSize: 13, color: '#555', marginTop: 12 }}>
        Busiest day is <strong>{peakDay}</strong> with <strong>{maxCount}</strong> orders verified.
      </p>
    );
  })()}
</Section>

{/* Outliers */}
<Section title="Flagging Longer Verifications">
  <p style={{ fontSize: 13, color: '#888', marginBottom: 16, marginTop: -8 }}>
    Orders that took longer than average
  </p>
  {(() => {
    const durations = stats.allVerifications.map(v => v.durationMinutes).filter(d => d > 0);
    if (durations.length < 3) return <p style={{ fontSize: 13, color: '#aaa' }}>Not enough data yet.</p>;

    const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
    const stdDev = Math.sqrt(durations.reduce((sum, d) => sum + Math.pow(d - avg, 2), 0) / durations.length);
    const threshold = avg + 1.5 * stdDev;

    const outliers = stats.allVerifications
      .filter(v => v.durationMinutes > threshold)
      .sort((a, b) => b.durationMinutes - a.durationMinutes)
      .slice(0, 10);

    if (outliers.length === 0) return (
      <p style={{ fontSize: 13, color: '#aaa' }}>No outliers detected — verifications are consistent.</p>
    );

    return (
      <>
        <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
          Threshold: <strong>{threshold.toFixed(1)} min</strong> (avg {avg.toFixed(1)} min + 1.5× std dev). Showing top {outliers.length}.
        </p>
        <table style={s.table}>
          <thead>
            <tr>
              {['Order', 'Name', 'Date', 'Verified By', 'Duration', 'Items'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {outliers.map((v, i) => (
              <tr key={i} style={s.tr}>
                <td style={s.td}>{v.orderId}</td>
                <td style={s.td}>{v.orderName || '—'}</td>
                <td style={s.td}>{v.dateVerified}</td>
                <td style={s.td}>{v.verifiedBy}</td>
                <td style={{ ...s.td, color: '#e25d5d', fontWeight: 600 }}>{v.durationMinutes.toFixed(1)} min</td>
                <td style={s.td}>{v.totalItems}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  })()}
</Section>
      <Section title="Verification Log">
        <VerificationLog msalInstance={msalInstance} />
      </Section>
      <ReceivingHistoryLog
        msalInstance={msalInstance}
        wrap={(content) => <Section title="PO Receiving Log">{content}</Section>}
      />
    </div>
  );
}

function Card({ label, value, accent }) {
  return (
    <div className="stats-card" style={{ ...s.card, borderColor: '#eee' }}>
      <div style={{ ...s.cardValue, color: accent }}>
        {value}
      </div>
      <div style={s.cardLabel}>{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="stats-section" style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

const s = {
  page: { padding: '32px 24px', maxWidth: 900, margin: '0 auto' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  themeToggle: { display: 'flex', gap: 10 },
  themeSwatch: { width: 22, height: 22, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0 },
  loading: { padding: 40, textAlign: 'center', color: '#888' },
  filterRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, flexWrap: 'wrap' },
  label: { fontSize: 13, fontWeight: 600, color: '#555' },
  dateInput: { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 },
  filterBtn: { padding: '6px 16px', background: 'rgb(65, 159, 196)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  clearBtn: { padding: '6px 16px', background: '#eee', color: '#333', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  cards: { display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' },
  card: { flex: 1, minWidth: 160, background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardValue: { fontSize: 32, fontWeight: 700, color: 'rgb(65, 159, 196)' },
  cardLabel: { fontSize: 13, color: '#888', marginTop: 4 },
  section: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 24, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  sectionTitle: { fontSize: 16, fontWeight: 700, marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 20 },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', borderBottom: '2px solid #eee' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '10px 12px', fontSize: 14 },
  globalBanner: { 
  background: 'linear-gradient(135deg, rgb(65, 159, 196), rgb(65, 159, 196))', 
  borderRadius: 10, 
  padding: '20px 28px', 
  marginBottom: 28, 
  display: 'flex', 
  flexDirection: 'column',   // 👈 was missing
  gap: 0,
  cursor: 'pointer', 
  userSelect: 'none' 
},globalTitle: { color: '#fff', fontWeight: 700, fontSize: 16 },
globalStats: { display: 'flex', alignItems: 'center', gap: 24 },
globalStat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
globalValue: { color: '#fff', fontSize: 28, fontWeight: 800, lineHeight: 1 },
globalLabel: { color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 4 },
csvBtn: { padding: '6px 16px', background: 'rgb(43, 106, 131)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, marginLeft: 'auto' },
globalDivider: { width: 1, height: 40, background: 'rgba(255,255,255,0.3)' },
bannerRow: { 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'space-between', 
  flexWrap: 'wrap', 
  gap: 16 
},
expandedRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.25)' },
expandedCard: { background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 },
expandedVal: { color: '#fff', fontSize: 22, fontWeight: 800, lineHeight: 1 },
expandedLbl: { color: 'rgba(255,255,255,0.8)', fontSize: 12 },
expandedSub: { color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2 },
};